import { useRef } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { AlignCenter, AlignLeft, AlignRight, Trash2 } from "lucide-react";

const ALIGNS = [
  { id: "left", label: "À gauche, texte à côté", Icon: AlignLeft },
  { id: "center", label: "Centrée", Icon: AlignCenter },
  { id: "right", label: "À droite, texte à côté", Icon: AlignRight },
];

/**
 * Image dans l'éditeur : poignée de redimensionnement (coin bas droit), alignement,
 * légende et source modifiables directement sous l'image.
 */
export default function FigureView({ node, updateAttributes, selected, deleteNode, editor }) {
  const { src, alt, caption, source, width, align } = node.attrs;
  const boxRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const editorWidth = editor.view.dom.clientWidth - 40; // largeur utile du texte
    const startX = e.clientX;
    const startW = boxRef.current.getBoundingClientRect().width;
    const sign = align === "right" ? -1 : 1;
    const move = (ev) => {
      const px = startW + sign * (ev.clientX - startX) * (align === "center" ? 2 : 1);
      const pct = Math.round(Math.min(100, Math.max(20, (px / editorWidth) * 100)) / 5) * 5;
      updateAttributes({ width: pct });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const editable = editor.isEditable;

  return (
    <NodeViewWrapper
      as="figure"
      data-figure=""
      data-align={align}
      style={{ width: `${width}%` }}
      className={`editor-figure ${selected ? "is-selected" : ""}`}
      data-drag-handle
    >
      <div ref={boxRef} className="relative group">
        <img src={src} alt={alt || caption || ""} draggable={false} />
        {editable && (
          <>
            <div className={`absolute top-2 left-2 flex items-center gap-1 rounded-md bg-black/75 backdrop-blur p-1 transition-opacity ${selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} contentEditable={false}>
              {ALIGNS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  title={label}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => updateAttributes({ align: id, width: id === "center" ? width : Math.min(width, 50) })}
                  className={`w-7 h-7 grid place-items-center rounded ${align === id ? "bg-white/20 text-white" : "text-zinc-300 hover:bg-white/10"}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </button>
              ))}
              <span className="px-1.5 text-[11px] text-zinc-300 tabular-nums">{width}%</span>
              <button type="button" title="Supprimer l'image" onMouseDown={(e) => e.preventDefault()} onClick={deleteNode} className="w-7 h-7 grid place-items-center rounded text-rose-300 hover:bg-rose-500/20">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <span
              onPointerDown={startResize}
              title="Glisser pour redimensionner"
              contentEditable={false}
              className={`absolute bottom-1.5 ${align === "right" ? "left-1.5 cursor-nesw-resize" : "right-1.5 cursor-nwse-resize"} w-4 h-4 rounded-sm bg-white border-2 border-emerald-500 shadow transition-opacity ${selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
            />
          </>
        )}
      </div>
      {editable ? (
        <figcaption contentEditable={false} className="editor-figcaption">
          <input
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            placeholder="Ajouter une légende…"
            className="w-full bg-transparent text-center outline-none placeholder:text-zinc-600"
          />
          <input
            value={source}
            onChange={(e) => updateAttributes({ source: e.target.value })}
            placeholder="Source (facultatif)"
            className="w-full bg-transparent text-center text-xs outline-none placeholder:text-zinc-700 text-zinc-500"
          />
        </figcaption>
      ) : (
        (caption || source) && (
          <figcaption>
            {caption && <span data-caption="">{caption}</span>}
            {source && <span data-source="">{source}</span>}
          </figcaption>
        )
      )}
    </NodeViewWrapper>
  );
}
